import { X, TrendingUp, TrendingDown, Minus } from "lucide-react";

const T = {
  en: { title: "Side-by-side comparison", hint: "Pin a second analysis to compare", before: "Before", after: "After", strengths: "Strengths", improvements: "Improvements", unpin: "Unpin" },
  fr: { title: "Comparaison côte à côte", hint: "Épingle une 2e analyse pour comparer", before: "Avant", after: "Après", strengths: "Points forts", improvements: "Axes d'amélioration", unpin: "Retirer" },
};

const Delta = ({ v }) => {
  if (v == null || isNaN(v)) return null;
  const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
  const cls = v > 0 ? "text-[#00F076]" : v < 0 ? "text-[#FF2E54]" : "text-slate-400";
  return <span className={`inline-flex items-center gap-0.5 font-mono-tech text-xs font-bold ${cls}`} data-testid="compare-delta"><Icon className="w-3 h-3" />{v > 0 ? "+" : ""}{v}</span>;
};

export const VideoCompare = ({ pinned, lang = "en", onUnpin }) => {
  const t = T[lang] || T.en;
  if (pinned.length === 0) return null;
  const sorted = [...pinned].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const [older, newer] = sorted;
  const keys = Array.from(new Set([...Object.keys(older?.analysis?.score || {}), ...Object.keys(newer?.analysis?.score || {})]));
  const fmt = (v) => new Date(v.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short" });

  return (
    <div className="rounded-xl border border-[#00F076]/40 bg-[#00F076]/5 p-5" data-testid="video-compare">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="font-display font-bold uppercase text-lg">{t.title}</div>
        {pinned.length < 2 && <div className="text-xs text-slate-400 font-mono-tech uppercase" data-testid="video-compare-hint">{t.hint}</div>}
      </div>
      <div className="grid md:grid-cols-2 gap-4 items-start">
        {[older, newer].map((v, i) => v ? (
          <div key={v.id} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-3" data-testid={`video-compare-slot-${i}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">{i === 0 ? t.before : t.after} · {fmt(v)}</div>
              <button onClick={() => onUnpin(v.id)} className="text-slate-500 hover:text-white" data-testid={`video-unpin-${v.id}`} title={t.unpin}><X className="w-3.5 h-3.5" /></button>
            </div>
            {v.thumbnail && <img src={v.thumbnail} alt="" className="mt-2 w-full aspect-video object-cover rounded" />}
            <div className="mt-2 text-sm font-semibold line-clamp-2">{v.analysis?.summary || v.filename}</div>
            <div className="mt-2 grid grid-cols-3 gap-1">
              {keys.map((k) => (
                <div key={k} className="text-center rounded bg-[#121721] border border-[#1E2638] p-1.5">
                  <div className="text-[9px] font-mono-tech uppercase text-slate-500">{k.replace("_", " ")}</div>
                  <div className="font-display font-black text-lg text-[#00F076]">{v.analysis?.score?.[k] ?? "—"}</div>
                  {i === 1 && older && <Delta v={(v.analysis?.score?.[k] ?? 0) - (older.analysis?.score?.[k] ?? 0)} />}
                </div>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
              <div><div className="font-mono-tech uppercase text-[#00F076] text-[9px] mb-0.5">{t.strengths}</div><ul className="text-slate-300 space-y-0.5">{(v.analysis?.strengths || []).slice(0, 3).map((s, j) => <li key={j}>• {s}</li>)}</ul></div>
              <div><div className="font-mono-tech uppercase text-amber-400 text-[9px] mb-0.5">{t.improvements}</div><ul className="text-slate-300 space-y-0.5">{(v.analysis?.improvements || []).slice(0, 3).map((s, j) => <li key={j}>• {s}</li>)}</ul></div>
            </div>
          </div>
        ) : (
          <div key="empty" className="rounded-lg border border-dashed border-[#1E2638] p-3 min-h-[200px] flex items-center justify-center text-slate-500 text-xs font-mono-tech uppercase text-center" data-testid="video-compare-empty">{t.hint}</div>
        ))}
      </div>
    </div>
  );
};
